'use client';

import KitAppIcon from '@omni/kit/components/KitAppIcon';
import KitButton from '@omni/kit/components/KitButton';
import KitDividerWithText from '@omni/kit/components/KitDividerWithText';
import KitInternationalPhoneNumberInput from '@omni/kit/components/KitInternationalPhoneNumberInput';
import { Country } from '@omni/kit/components/KitInternationalPhoneNumberInput/constants/types';
import KitLink from '@omni/kit/components/KitLink';
import KitLoader from '@omni/kit/components/KitLoader';
import KitText from '@omni/kit/components/KitText';
import Show from '@omni/kit/components/Show';
import { SUBSPLASH_AUTH_PROVIDER_ID } from '@omni/kit/constants/identifiers';
import { SUBSPLASH_PHONE_AUTH_PROVIDER_ID } from '@omni/kit/constants/identifiers';
import {
  GoogleCaptchaProvider,
  useCaptcha,
} from '@omni/kit/contexts/GoogleCaptchaContext';
import { useShellContext } from '@omni/kit/contexts/ShellContext';
import { SizeClassV2, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import { useSourceContext } from '@omni/kit/contexts/SourceContext';
import { useSupportedCountries } from '@omni/kit/hooks/useSupportedCountries';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import React, { ReactElement, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { Platform, StyleSheet, View } from 'react-native';

import GetAppHeaders from '../Downloader/GetAppHeaders';
import Environment, { IS_LOCALHOST } from '../Environment';
import HealthMonitor from '../HealthMonitor';
import KitSnack from '../components/KitSnack';
import useShellAuthProviders from '../contexts/ShellContext/hooks/useShellAuthProviders';
import BorderRadius from '../theming/BorderRadius';
import {
  capitalizeFirstLetter,
  handleErrorResponse,
} from '../utilities/utilities';
import AuthFooter from './AuthFooter';
import AuthenticatorNotFoundScreen from './AuthenticatorNotFoundScreen';
import { LoginButtons } from './LoginButtons';
import { AuthProvider } from './Service/Types';
import VerifyCodeScreen from './VerifyCodeScreen';

const debug = require('debug')('tca:auth:AuthProviderList');

interface AuthProviderListProps {
  appKey: string;
  customReturnUrl?: string;
  customHeader?: ReactElement;
  appBrandingUrl?: string;
  openInNewTab?: boolean;
  orgKey?: string;
  targetAuthProviderId: string;
  showFooter?: boolean;
}

export default function AuthProviderList({
  appKey,
  customReturnUrl,
  customHeader,
  appBrandingUrl,
  openInNewTab,
  targetAuthProviderId,
  showFooter = true,
}: AuthProviderListProps): ReactElement {
  const [appHeaders, setAppHeaders] = useState<Record<string, string>>({});
  const [verifyCodeScreenVisible, setVerifyCodeScreenVisible] = useState(false);
  const [
    authenticatorNotFoundScreenVisible,
    setAuthenticatorNotFoundScreenVisible,
  ] = useState(false);
  const [authUrl, setAuthUrl] = useState('');
  const [confirmEmailOtpUrl, setConfirmEmailOtpUrl] = useState('');
  const [confirmPhoneOtpUrl, setConfirmPhoneOtpUrl] = useState('');
  const [connectAuthenticatorUrl, setConnectAuthenticatorUrl] = useState('');
  const [emailOtpAuthState, setEmailOtpAuthState] = useState('');
  const [identityAccessToken, setIdentityAccessToken] = useState('');
  const [identityRefreshToken, setIdentityRefreshToken] = useState('');
  const [phoneAuthenticatorId, setPhoneAuthenticatorId] = useState('');
  const [phoneOtpAuthState, setPhoneOtpAuthState] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [callingCode, setCallingCode] = useState('');
  const [regionCode, setRegionCode] = useState('');
  const [firstNameValue, setFirstNameValue] = useState('');
  const [lastNameValue, setLastNameValue] = useState('');
  const [email, setEmail] = useState<string>('');

  const { sizeClassV2 } = useSizeClass();
  const isMobileDevice = sizeClassV2 < SizeClassV2.M;

  const source = useSourceContext();

  const { isFetchingAuthProviders, loginEnabledProviders, providers } =
    useShellAuthProviders();

  const { appUrls } = useShellContext();
  const loginWithPhoneSupportedCountriesUrl =
    Platform.OS === 'web'
      ? `${Environment.feedsHost}/api/v1/login-with-phone-supported-countries`
      : appUrls?.loginWithPhoneSupportedCountriesUrl;

  const targetProvider = providers.find(
    (c) => c.authproviderid === targetAuthProviderId
  );

  const hasPhoneAuthProviderEnabled = loginEnabledProviders.some(
    (provider) => provider.authproviderid === SUBSPLASH_PHONE_AUTH_PROVIDER_ID
  );

  // Enterprise apps with custom login providers will not show the Subsplash branding (e.g. Ligonier and Wild at Heart)
  const suppressSubsplashBranding =
    loginEnabledProviders.length > 0 &&
    providers.find(
      (authProvider) =>
        authProvider.authproviderid === SUBSPLASH_AUTH_PROVIDER_ID &&
        !authProvider.enable_login
    );

  const containerStyle = [
    styles.container,
    isMobileDevice
      ? {
          maxWidth: 455,
        }
      : {
          maxWidth: 420,
        },
  ];

  const reset = () => {
    setPhoneNumber('');
    setFirstNameValue('');
    setLastNameValue('');
    setEmail('');
    setVerifyCodeScreenVisible(false);
    setAuthenticatorNotFoundScreenVisible(false);
  };

  useEffect(() => {
    const getAppHeaders = async () => {
      const headers = await GetAppHeaders();
      setAppHeaders(headers);
    };
    getAppHeaders();
  }, []);

  return (
    <GoogleCaptchaProvider feature='endUserAuth' hideBadge>
      <View
        style={{
          alignItems: 'center',
          backgroundColor: Colors.N0,
          justifyContent: 'center',
          flexGrow: 1, // needed to fill the remaining space
          paddingTop: Spacing.xl,
        }}
      >
        {authenticatorNotFoundScreenVisible ? (
          <View style={containerStyle}>
            <AuthenticatorNotFoundScreen
              email={email}
              firstNameValue={firstNameValue}
              lastNameValue={lastNameValue}
              phoneAuthenticatorId={phoneAuthenticatorId}
              reset={reset}
              setAuthenticatorNotFoundScreenVisible={
                setAuthenticatorNotFoundScreenVisible
              }
              setConfirmEmailOtpUrl={setConfirmEmailOtpUrl}
              setEmail={setEmail}
              setEmailOtpAuthState={setEmailOtpAuthState}
              setFirstNameValue={setFirstNameValue}
              setLastNameValue={setLastNameValue}
              setVerifyCodeScreenVisible={setVerifyCodeScreenVisible}
              showFooter={showFooter}
              suppressSubsplashBranding={Boolean(suppressSubsplashBranding)}
            />
          </View>
        ) : verifyCodeScreenVisible ? (
          <View style={containerStyle}>
            <VerifyCodeScreen
              appHeaders={appHeaders}
              authUrl={authUrl}
              callingCode={callingCode}
              confirmEmailOtpUrl={confirmEmailOtpUrl}
              confirmPhoneOtpUrl={confirmPhoneOtpUrl}
              connectAuthenticatorUrl={connectAuthenticatorUrl}
              email={email}
              emailOtpAuthState={emailOtpAuthState}
              firstNameValue={firstNameValue}
              identityAccessToken={identityAccessToken}
              identityRefreshToken={identityRefreshToken}
              lastNameValue={lastNameValue}
              phoneAuthenticatorId={phoneAuthenticatorId}
              phoneNumber={phoneNumber}
              phoneOtpAuthState={phoneOtpAuthState}
              regionCode={regionCode}
              reset={reset}
              setAuthenticatorNotFoundScreenVisible={
                setAuthenticatorNotFoundScreenVisible
              }
              setConnectAuthenticatorUrl={setConnectAuthenticatorUrl}
              setIdentityAccessToken={setIdentityAccessToken}
              setIdentityRefreshToken={setIdentityRefreshToken}
              setPhoneAuthenticatorId={setPhoneAuthenticatorId}
              setVerifyCodeScreenVisible={setVerifyCodeScreenVisible}
              showFooter={showFooter}
              suppressSubsplashBranding={Boolean(suppressSubsplashBranding)}
            />
          </View>
        ) : isFetchingAuthProviders ? (
          <View style={styles.loadingStyle}>
            <KitLoader />
          </View>
        ) : (
          <View style={containerStyle}>
            <View
              style={[
                styles.authProviderContainer,
                suppressSubsplashBranding && { marginBottom: 0 },
                isMobileDevice && { maxWidth: 350 },
              ]}
            >
              {customHeader || <LoginHeader appBrandingUrl={appBrandingUrl} />}
              {hasPhoneAuthProviderEnabled ? (
                <LoginWithPhone
                  appHeaders={appHeaders}
                  loginEnabledProviders={loginEnabledProviders}
                  loginWithPhoneSupportedCountriesUrl={
                    loginWithPhoneSupportedCountriesUrl
                  }
                  phoneNumber={phoneNumber}
                  setAuthUrl={setAuthUrl}
                  setConfirmPhoneOtpUrl={setConfirmPhoneOtpUrl}
                  setPhoneOtpAuthState={setPhoneOtpAuthState}
                  setPhoneNumber={setPhoneNumber}
                  setCallingCode={setCallingCode}
                  setRegionCode={setRegionCode}
                  setVerifyCodeScreenVisible={setVerifyCodeScreenVisible}
                />
              ) : null}
              <View style={styles.authProvider}>
                <LoginButtons
                  appKey={appKey}
                  customReturnUrl={customReturnUrl}
                  loginEnabledProviders={loginEnabledProviders || []}
                  openInNewTab={openInNewTab}
                  source={source}
                  targetProvider={targetProvider}
                />
                {!suppressSubsplashBranding ? (
                  <KitText
                    center
                    fontSize={14}
                    style={{
                      marginTop: Spacing.xs,
                      paddingTop: Spacing.xxs,
                    }}
                  >
                    <Trans
                      i18nKey='common:subsplashDisclaimer'
                      components={{
                        termsLink: (
                          <KitLink
                            href='https://www.subsplash.com/legal/terms-of-use'
                            fontSize={14}
                          />
                        ),
                        privacyLink: (
                          <KitLink
                            href='https://www.subsplash.com/legal/privacy'
                            fontSize={14}
                          />
                        ),
                      }}
                    />
                  </KitText>
                ) : null}
              </View>
              <AuthFooter
                showFooter={showFooter}
                suppressSubsplashBranding={Boolean(suppressSubsplashBranding)}
                showRecaptcha={hasPhoneAuthProviderEnabled}
              />
            </View>
          </View>
        )}
      </View>
    </GoogleCaptchaProvider>
  );
}

const LoginHeader = ({ appBrandingUrl }: { appBrandingUrl?: string }) => {
  const { t } = useTranslation(['auth']);
  const { app } = useShellContext();

  return (
    <>
      <KitAppIcon
        size={64}
        imageUrl={appBrandingUrl || app?.branding?.mobileIcon}
      />
      <KitText center h1 style={{ marginTop: Spacing.m }}>
        {t('auth:loginHeaderTitle')}
      </KitText>
    </>
  );
};

const LoginWithPhone = ({
  appHeaders,
  loginEnabledProviders,
  loginWithPhoneSupportedCountriesUrl,
  phoneNumber,
  setAuthUrl,
  setConfirmPhoneOtpUrl,
  setPhoneOtpAuthState,
  setPhoneNumber,
  setCallingCode,
  setRegionCode,
  setVerifyCodeScreenVisible,
}: {
  appHeaders: Record<string, string>;
  loginEnabledProviders: AuthProvider[];
  loginWithPhoneSupportedCountriesUrl?: string;
  phoneNumber: string;
  setAuthUrl: (url: string) => void;
  setConfirmPhoneOtpUrl: (url: string) => void;
  setPhoneOtpAuthState: (state: string) => void;
  setPhoneNumber: (number: string) => void;
  setCallingCode: (code: string) => void;
  setRegionCode: (code: string) => void;
  setVerifyCodeScreenVisible: (visible: boolean) => void;
}) => {
  const authUrl = loginEnabledProviders.find(
    (provider) => provider.authproviderid === SUBSPLASH_PHONE_AUTH_PROVIDER_ID
  )?.auth_url;
  const { t } = useTranslation(['auth']);

  const { supportedCountries, firstSupportedCountry, isLoadingCountries } =
    useSupportedCountries(loginWithPhoneSupportedCountriesUrl);

  const { fetchCaptcha, siteKey } = useCaptcha();
  const { tokens } = useShellContext();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | undefined>(
    firstSupportedCountry
  );
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleCountryChange = (country: Country) => {
    setSelectedCountry(country);
  };

  const isValidPhoneNumber = (phoneNumber: string) => {
    const significant = phoneNumber.replace(/\D/g, '');

    return significant.length === selectedCountry?.length;
  };

  const authorizePhoneNumber = async () => {
    if (authUrl) {
      try {
        setIsLoading(true);
        setShowError(false);
        setErrorMessage('');

        const response = await fetch(authUrl, {
          method: 'POST',
          body: JSON.stringify({
            phone_number: {
              significant: phoneNumber.replace(/\D/g, ''),
              country: {
                region_code: selectedCountry?.regionCode,
                calling_code: selectedCountry?.callingCode,
              },
            },
            verification_site_key: siteKey,
            verification_token: await fetchCaptcha('authorize_phone'),
            ...(Platform.OS === 'web' && {
              skip_deep_link: true,
              use_refresh_cookie: !IS_LOCALHOST,
            }),
          }),
          headers: {
            ...appHeaders,
            Authorization: `Bearer ${tokens.guest}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const responseData = await response.json();
          const { confirm_url: confirmPhoneOtpUrl, state: phoneOtpAuthState } =
            responseData;

          setAuthUrl(authUrl);
          setConfirmPhoneOtpUrl(confirmPhoneOtpUrl);
          setPhoneOtpAuthState(phoneOtpAuthState);
          setPhoneNumber(phoneNumber);
          setCallingCode(selectedCountry?.callingCode ?? '');
          setRegionCode(selectedCountry?.regionCode ?? '');
          setVerifyCodeScreenVisible(true);
        } else {
          const { status, message, data } = await handleErrorResponse(response);
          const invalidPhoneNumberError =
            status === 400 && message?.includes('invalid phone number');

          if (status !== 429 && !invalidPhoneNumberError) {
            HealthMonitor.logError(data, {
              tags: {
                label: 'auth:failed to authorize phone',
              },
            });
          }

          if (invalidPhoneNumberError) {
            setErrorMessage(t('auth:error.enterValidPhoneNumber'));
            setShowError(true);
          } else {
            KitSnack.show(
              t('common:oopsSomethingWentWrongPleaseTryAgainLater')
            );
          }
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <KitInternationalPhoneNumberInput
        label={t('profile:textMobilePhoneNumber')}
        subtext={t('auth:wellTextYouToConfirmYourNumber')}
        countries={supportedCountries}
        selectedCountry={selectedCountry}
        onCountryChange={(country) => {
          handleCountryChange(country);
          setShowError(false);
        }}
        value={phoneNumber}
        onChangeText={(text) => {
          setPhoneNumber(text);
          setShowError(false);
        }}
        returnKeyType='go'
        blurOnSubmit
        onSubmitEditing={() => {
          if (
            isValidPhoneNumber(phoneNumber) &&
            !isLoading &&
            !isLoadingCountries
          ) {
            authorizePhoneNumber();
          }
        }}
        style={styles.phoneInputContainer}
      />
      <Show show={showError}>
        <View style={styles.errorContainer}>
          <KitText color={Colors.N0} fontSize={16}>
            {errorMessage}
          </KitText>
        </View>
      </Show>
      <KitButton
        title={t('common:buttonTitleContinue')}
        onPress={authorizePhoneNumber}
        isLoading={isLoading}
        color={Colors.N900}
        style={styles.phoneButton}
        disabled={
          !isValidPhoneNumber(phoneNumber) || isLoading || isLoadingCountries
        }
      />
      <KitDividerWithText text={t('common:or')} />
    </>
  );
};

const styles = StyleSheet.create({
  appleWarningBox: {
    alignItems: 'center',
    backgroundColor: Colors.N50,
    borderRadius: 6,
    flexDirection: 'row',
    marginTop: Spacing.xxl,
    maxWidth: 423,
    padding: Spacing.l,
  },
  authProvider: {
    marginTop: Spacing.xl,
    width: '100%',
  },
  authProviderContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: Spacing.l,
    paddingHorizontal: Spacing.s,
    width: '100%',
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '100%',
  },
  brandingContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  loadingStyle: {
    alignItems: 'center',
    minHeight: 200,
    justifyContent: 'center',
  },
  phoneButton: {
    width: '100%',
    marginBottom: Spacing.xl,
  },
  phoneInputContainer: {
    width: '100%',
    marginTop: Spacing.xl,
    marginBottom: Spacing.xl,
  },
  errorContainer: {
    paddingHorizontal: Spacing.l,
    paddingVertical: Spacing.m,
    backgroundColor: Colors.R500,
    borderRadius: BorderRadius.m,
    marginBottom: Spacing.l,
    width: '100%',
  },
});
